import React, { useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { Header } from './components/Header';
import { Sidebar } from './components/Sidebar';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { LandingPage } from './pages/LandingPage';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { AmazonCallback } from './pages/AmazonCallback';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { loginRedirect, safeNextPath } from './utils/safePath';

const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Orders = lazy(() => import('./pages/Orders').then(m => ({ default: m.Orders })));
const Replenishables = lazy(() => import('./pages/Replenishables').then(m => ({ default: m.Replenishables })));
const Returns = lazy(() => import('./pages/Returns').then(m => ({ default: m.Returns })));
const InventoryCosts = lazy(() => import('./pages/InventoryCosts').then(m => ({ default: m.InventoryCosts })));
const InventoryInsights = lazy(() => import('./pages/InventoryInsights').then(m => ({ default: m.InventoryInsights })));
const Shipments = lazy(() => import('./pages/Shipments').then(m => ({ default: m.Shipments })));
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));

const PageFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cashy-purple-500" />
  </div>
);

const LazyPage: React.FC<{ Page: React.LazyExoticComponent<React.ComponentType> }> = ({ Page }) => (
  <ErrorBoundary>
    <Suspense fallback={<PageFallback />}>
      <Page />
    </Suspense>
  </ErrorBoundary>
);

const AuthenticatedApp: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <Header />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <main className="flex-1 transition-all duration-300 min-h-[calc(100vh-4rem)] overflow-y-auto bg-gray-900 page-container lg:ml-16">
        <div className="p-6 pt-24">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

const AuthRoutes: React.FC = () => {
  const { user, isLoading, error, isRecovery } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cashy-purple-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Configuration Error</h1>
          <p className="text-gray-300 mb-6">{error}</p>
          <div className="bg-gray-800 rounded-lg p-4 text-left">
            <h3 className="text-white font-semibold mb-2">To fix this:</h3>
            <ol className="text-gray-300 text-sm space-y-1">
              <li>1. Create a <code className="bg-gray-700 px-1 rounded">.env</code> file in the project root</li>
              <li>2. Add your Supabase URL and API key</li>
              <li>3. Restart the development server</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  if (isRecovery) {
    return (
      <Routes>
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to="/reset-password" replace />} />
      </Routes>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to={loginRedirect(location.pathname, location.search)} replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/login" element={<Navigate to={safeNextPath(new URLSearchParams(location.search).get('next'))} replace />} />
      <Route path="/signup" element={<Navigate to="/dashboard" replace />} />
      <Route path="/forgot-password" element={<Navigate to="/dashboard" replace />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/amazon/callback" element={<AmazonCallback />} />
      <Route element={<AuthenticatedApp />}>
        <Route path="/dashboard" element={<LazyPage Page={Dashboard} />} />
        <Route path="/orders" element={<LazyPage Page={Orders} />} />
        <Route path="/replenishables" element={<LazyPage Page={Replenishables} />} />
        <Route path="/returns" element={<LazyPage Page={Returns} />} />
        <Route path="/data-upload" element={<Navigate to="/dashboard" replace />} />
        <Route path="/inventory-costs" element={<LazyPage Page={InventoryCosts} />} />
        <Route path="/ai-insights" element={<Navigate to="/dashboard" replace />} />
        <Route path="/inventory-insights" element={<LazyPage Page={InventoryInsights} />} />
        <Route path="/shipments" element={<LazyPage Page={Shipments} />} />
        <Route path="/historic-orders" element={<Navigate to="/dashboard" replace />} />
        <Route path="/settings" element={<LazyPage Page={Settings} />} />
        <Route path="/profit-calculator" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
};

const AppContent: React.FC = () => (
  <Router>
    <AuthRoutes />
  </Router>
);

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
